import { useState, type ReactNode } from 'react';
import { ActionDialog, TextInput } from './ActionDialog';
import type { ActionState, Channel, ChannelInput, InvoiceChannelView } from './pb-types';
import { channelHint, channelWord, companiesWord, day } from './pb-words';

export interface ChannelDialogProps extends ActionState {
  account: string;
  current: InvoiceChannelView;
  /** The companies the API named when it refused for companies billed the other way (409 `channel_has_online_items`). */
  refused: { tenantId: string; renewsOn: string | null }[] | null;
  onConfirm: (input: ChannelInput) => void;
  onClose: () => void;
}

type Move = 'none' | 'at_renewal';

/** The dialog's answers, and the ask they make once whole (null while one is missing or out of range). */
function useChannelForm(p: ChannelDialogProps) {
  const other: Channel = p.current.channel === 'quickbooks' ? 'stripe' : 'quickbooks';
  const [channel, setChannel] = useState<Channel>(other);
  const [customer, setCustomer] = useState(p.current.quickbooksCustomer ?? p.account);
  const [terms, setTerms] = useState(String(p.current.termsDays));
  const [move, setMove] = useState<Move>(p.refused ? 'at_renewal' : 'none');
  const days = Number(terms);
  const named = customer.trim();
  const ok =
    channel !== p.current.channel &&
    Number.isInteger(days) &&
    days >= 0 &&
    days <= 90 &&
    (channel === 'stripe' || (named.length >= 1 && named.length <= 100));
  const ask = (reason: string): ChannelInput => ({
    ...{ channel, quickbooksCustomer: channel === 'quickbooks' ? named : null },
    ...{ termsDays: days, moveExisting: move, reason },
  });
  return { channel, setChannel, customer, setCustomer, terms, setTerms, move, setMove, ok, ask };
}

/** Change channel (ADR-0036, section 3): the account's invoice, its QuickBooks customer and terms, and what happens to companies billed the other way. */
export function ChannelDialog(p: ChannelDialogProps): ReactNode {
  const f = useChannelForm(p);
  return (
    <ActionDialog
      {...p}
      size="lg"
      title={`Change how ${p.account} is invoiced`}
      confirmLabel={`Switch to ${channelWord[f.channel]}`}
      ready={f.ok}
      onConfirm={(reason) => p.onConfirm(f.ask(reason))}
      consequence={<ChannelConsequence to={f.channel} move={f.move} refused={p.refused} />}
    >
      <ChannelChoice value={f.channel} now={p.current.channel} onChange={f.setChannel} />
      <div className="pb-grid2">
        {f.channel === 'quickbooks' ? (
          <TextInput
            label="QuickBooks customer"
            hint="Exactly as in Ademero's QuickBooks"
            value={f.customer}
            onChange={f.setCustomer}
            max={100}
            required
          />
        ) : null}
        <TextInput
          label="Terms (days to pay)"
          type="number"
          inputMode="numeric"
          value={f.terms}
          onChange={f.setTerms}
          hint="0 to 90"
        />
      </div>
      <MoveChoice move={f.move} onMove={f.setMove} />
    </ActionDialog>
  );
}

function ChannelChoice(p: {
  value: Channel;
  now: Channel;
  onChange: (c: Channel) => void;
}): ReactNode {
  return (
    <fieldset className="pb-choices">
      <legend>Invoice</legend>
      {(['quickbooks', 'stripe'] as Channel[]).map((c) => (
        <label key={c} className="pb-choice">
          <input
            type="radio"
            name="pb-channel"
            checked={p.value === c}
            onChange={() => p.onChange(c)}
          />
          <span>
            {channelWord[c]}
            {c === p.now ? ' (now)' : ''}
            <small>{channelHint[c]}</small>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

function MoveChoice({ move, onMove }: { move: Move; onMove: (m: Move) => void }): ReactNode {
  const choice = (m: Move, title: string, note: string) => (
    <label className="pb-choice">
      <input type="radio" name="pb-move" checked={move === m} onChange={() => onMove(m)} />
      <span>
        {title}
        <small>{note}</small>
      </span>
    </label>
  );
  return (
    <fieldset className="pb-choices">
      <legend>Companies billed the other way now</legend>
      {choice(
        'none',
        'Switch only if there are none',
        'Refused, naming them, if any company is still billed the other way.',
      )}
      {choice(
        'at_renewal',
        'Move each at its renewal',
        'Each keeps its current invoice to the end of its paid period, then moves.',
      )}
    </fieldset>
  );
}

function ChannelConsequence(p: {
  to: Channel;
  move: Move;
  refused: ChannelDialogProps['refused'];
}): ReactNode {
  return (
    <>
      <p>
        New companies the account takes on from now go on the{' '}
        <strong>{channelWord[p.to].toLowerCase()}</strong>.
        {p.to === 'quickbooks'
          ? ' Paige keeps each period and issues one statement a month on the 1st; accounting imports it into QuickBooks.'
          : ' Stripe invoices them; issued statements stay, and the open one still issues at the month’s end with what it holds.'}
      </p>
      {p.move === 'at_renewal' ? (
        <p>
          Companies billed the other way move <strong>at their renewal</strong>: the period already
          paid is served to its end and the next is on the new invoice, so nothing is billed twice
          and no day goes unbilled. Open online invoices stay owed.
        </p>
      ) : (
        <p>
          If any company is still billed the other way, the switch is refused and they are named.
        </p>
      )}
      {p.refused ? (
        <p role="status">
          {companiesWord(p.refused.length)} billed the other way:{' '}
          {p.refused.map((c) => (c.renewsOn ? `renews ${day(c.renewsOn)}` : c.tenantId)).join('; ')}
          .
        </p>
      ) : null}
      {p.to === 'stripe' ? (
        <p>Refused while any company is on Enterprise: move or end those first.</p>
      ) : null}
    </>
  );
}
